/**
 * The shared frame wrapper every surface renders: chrome config, context
 * menu, gesture target with hover chrome, streaming effects and the frame
 * body. Whatever
 * depends on the surface (focus, geometry, close, resize controls, detail
 * level) comes from the {@link FrameRenderer} in context.
 */

import { Suspense, memo, useRef, type ComponentType } from "react";
import { cn } from "@/lib/cn";
import { CollapsedFocusFrameTitle } from "./collapsed-focus-frame-title";
import { FrameChromeConfigProvider } from "./frame-chrome-config";
import { FrameDetailLoading } from "./frame-detail-loading";
import { FrameHoverChrome } from "./frame-hover-chrome";
import { FrameLazyFallback } from "./frame-lazy-fallback";
import { framePropsAreEqual, type FrameProps } from "./frame-props";
import { useFrameRenderer } from "./frame-renderer-context";
import { useFrameHoverActivation } from "./use-frame-hover-activation";
import { withContextMenu } from "./with-context-menu";
import { withStreamingEffects } from "./with-streaming-effects";

function displayName(component: ComponentType<FrameProps>): string {
  return component.displayName ?? component.name ?? "Component";
}

/** The pointer target that owns hover activation, chrome and surface controls. */
function withFrameGestureTarget(
  FrameComponent: ComponentType<FrameProps>,
): ComponentType<FrameProps> {
  function FrameWithGestureTarget(props: FrameProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const renderer = useFrameRenderer();
    const {
      focused,
      maximized,
      stackCollapsed,
      stackOverflowHidden,
      overflowTitleCount,
    } = renderer.useChromeState(props.id);
    const windowed = renderer.closeFrame !== undefined;
    const {
      bottomActive,
      topActive,
      handlePointerLeave,
      handlePointerMove,
      updateBottomActive,
      updateTopActive,
      snoozeBottom,
    } = useFrameHoverActivation(rootRef, focused, maximized);

    return (
      <div
        ref={rootRef}
        className={cn(
          "relative h-full w-full",
          stackOverflowHidden &&
            "pointer-events-none invisible overflow-hidden",
        )}
        data-canvas-frame-id={props.id}
        data-floating-frame-id={windowed ? props.id : undefined}
        data-frame-type={props.data.frameType}
        data-frame-focus={focused ? "true" : undefined}
        data-frame-bottom-active={bottomActive ? "true" : "false"}
        data-frame-conversation-read-only={
          props.data.frameType === "conversation" &&
          props.data.metadata?.conversationReadOnly === true
            ? "true"
            : "false"
        }
        data-frame-top-active={topActive ? "true" : "false"}
        data-interactive="true"
        data-testid={`frame-gesture-target-${props.id}`}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      >
        {renderer.renderControls?.({
          frameId: props.id,
          frameType: props.data.frameType,
          visible:
            props.data.status !== "streaming" &&
            !stackCollapsed &&
            !stackOverflowHidden,
        }) ?? null}
        <div
          {...(stackCollapsed || stackOverflowHidden
            ? {
                className:
                  "pointer-events-none invisible absolute inset-0 h-full w-full overflow-hidden",
                "aria-hidden": true,
                inert: true,
              }
            : { className: "contents" })}
        >
          <Suspense
            fallback={
              <FrameLazyFallback
                frameId={props.id}
                frameType={props.data.frameType}
                selected={props.selected}
              />
            }
          >
            <FrameComponent {...props} />
          </Suspense>
        </div>
        {stackCollapsed ? (
          <CollapsedFocusFrameTitle
            frameId={props.id}
            frame={props.data}
            overflowCount={overflowTitleCount}
          />
        ) : stackOverflowHidden ? null : (
          <FrameHoverChrome
            frameId={props.id}
            frame={props.data}
            frameRootRef={rootRef}
            bottomActive={bottomActive}
            topActive={topActive}
            onBottomActiveChange={updateBottomActive}
            onTopActiveChange={updateTopActive}
            snoozeBottom={snoozeBottom}
          />
        )}
      </div>
    );
  }

  FrameWithGestureTarget.displayName = `withFrameGestureTarget(${displayName(
    FrameComponent,
  )})`;

  return FrameWithGestureTarget;
}

/** One chrome config per frame, read by its hover pill and its menus. */
function withChromeConfig(
  FrameComponent: ComponentType<FrameProps>,
): ComponentType<FrameProps> {
  function FrameWithChromeConfig(props: FrameProps) {
    return (
      <FrameChromeConfigProvider>
        <FrameComponent {...props} />
      </FrameChromeConfigProvider>
    );
  }

  FrameWithChromeConfig.displayName = `withChromeConfig(${displayName(
    FrameComponent,
  )})`;

  return FrameWithChromeConfig;
}

/**
 * The frame body, or what the surface renders instead of it. A card-only
 * frame mounts no body until its detail has loaded.
 */
function withRendererBody(
  FrameComponent: ComponentType<FrameProps>,
): ComponentType<FrameProps> {
  function FrameRendererBody(props: FrameProps) {
    const renderer = useFrameRenderer();
    const body =
      props.data.contentState === "card" ? (
        <FrameDetailLoading frame={props.data} />
      ) : (
        <FrameComponent {...props} />
      );
    return renderer.renderBody ? renderer.renderBody(props, body) : body;
  }

  FrameRendererBody.displayName = `withRendererBody(${displayName(
    FrameComponent,
  )})`;

  return FrameRendererBody;
}

/**
 * Apply the shared frame wrappers, then memoize the whole chain on the frame
 * props alone, so a surface re-rendering its nodes (a canvas pan) does not
 * re-render the frame.
 */
export function withFrameChrome(
  FrameComponent: ComponentType<FrameProps>,
): ComponentType<FrameProps> {
  return memo(
    withChromeConfig(
      withContextMenu(
        withFrameGestureTarget(
          withStreamingEffects(withRendererBody(FrameComponent)),
        ),
      ),
    ),
    framePropsAreEqual,
  );
}
